import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Wand2, Sparkles, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "qualified", "proposal", "won", "lost"];
const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "client", label: "Client" },
  { key: "sponsorship", label: "Sponsorship" },
  { key: "collaboration", label: "Collaboration" },
  { key: "branding", label: "Branding" },
];

const Bar = ({ value }) => (
  <div className="h-1.5 bg-white/5 w-full">
    <div className="h-full bg-kinetic" style={{ width: `${value}%` }} />
  </div>
);

export default function CRM() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", category: "client", bant_budget: 50, bant_authority: 50, bant_need: 50, bant_timeline: 50, notes: "" });

  const load = async () => setItems(await api.listLeads());
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.createLead(form);
    setOpen(false);
    setForm({ name: "", company: "", email: "", category: "client", bant_budget: 50, bant_authority: 50, bant_need: 50, bant_timeline: 50, notes: "" });
    await load();
    toast.success("Lead added");
  };

  const updateStatus = async (id, status) => {
    await api.updateLead(id, { status });
    await load();
  };

  const del = async (id) => { await api.deleteLead(id); await load(); };

  const convert = async (id) => {
    try {
      const p = await import("@/lib/api").then(m => m.default.post(`/leads/${id}/convert`).then(r => r.data));
      await load();
      toast.success(`Project created: ${p.title}`);
    } catch { toast.error("Convert failed"); }
  };

  const triage = async (id) => {
    toast.info("AI triaging…");
    try {
      const r = await import("@/lib/api").then(m => m.default.post(`/leads/${id}/triage`).then(r => r.data));
      await load();
      const draft = r?.triage?.reply_draft;
      if (draft) {
        const ok = window.confirm("AI BANT + category updated. Show reply draft? (OK to view & copy)");
        if (ok) {
          try { await navigator.clipboard.writeText(draft); toast.success("Reply draft copied to clipboard"); } catch { /* ignore */ }
          window.alert(draft);
        }
      } else {
        toast.success("AI triage done");
      }
    } catch { toast.error("Triage failed"); }
  };

  return (
    <div className="space-y-6" data-testid="crm">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-white/40">INBOX · LEADS</div>
          <h1 className="text-4xl font-extrabold mt-2">Inbox</h1>
          <p className="text-white/60 mt-2">Clients · Sponsorships · Collaborations · Branding · scored with BANT.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-lead-btn" className="kinetic-btn rounded-sm"><Plus className="w-4 h-4 mr-2" /> New Lead</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0C0C0C] border-white/10 text-white rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-sans">New Lead</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["name", "Name"], ["company", "Company"], ["email", "Email"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="mono text-white/60">{l}</Label>
                  <Input data-testid={`lead-${k}`} value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} className="bg-black border-white/15 rounded-sm" />
                </div>
              ))}
              {[
                ["bant_budget", "Budget"], ["bant_authority", "Authority"], ["bant_need", "Need"], ["bant_timeline", "Timeline"]
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="mono text-white/60 flex justify-between">{l}<span>{form[k]}</span></Label>
                  <Slider data-testid={`slider-${k}`} value={[form[k]]} max={100} step={5} onValueChange={(v) => setForm(f => ({ ...f, [k]: v[0] }))} className="mt-2"/>
                </div>
              ))}
              <div>
                <Label className="mono text-white/60">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-black border-white/15 rounded-sm" />
              </div>
            </div>
            <DialogFooter><Button data-testid="lead-submit" onClick={create} className="kinetic-btn rounded-sm">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-1 flex-wrap">
        {CATEGORIES.map(c => {
          const count = c.key === "all" ? items.length : items.filter(l => (l.category || "client") === c.key).length;
          return (
            <button key={c.key} data-testid={`tab-${c.key}`} onClick={() => setTab(c.key)}
              className={`mono px-4 h-9 rounded-sm border ${tab === c.key ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/60 hover:text-white"}`}>
              {c.label.toUpperCase()} · {count}
            </button>
          );
        })}
      </div>

      <div className="border border-white/10 bg-[#0C0C0C]">
        <div className="grid grid-cols-[1.4fr_2fr_1fr_auto] gap-4 px-5 py-3 border-b border-white/10 mono text-white/40">
          <div>LEAD</div><div>BANT</div><div>STATUS</div><div></div>
        </div>
        {items.length === 0 && <div className="p-10 text-center text-white/40">NO LEADS YET</div>}
        <div className="divide-y divide-white/10">
          {items.filter(l => tab === "all" || (l.category || "client") === tab).map(l => {
            const avg = Math.round((l.bant_budget + l.bant_authority + l.bant_need + l.bant_timeline) / 4);
            return (
              <div key={l.id} className="grid grid-cols-[1.4fr_2fr_1fr_auto] gap-4 px-5 py-4 items-center" data-testid={`lead-row-${l.id}`}>
                <div>
                  <div className="font-sans font-bold">{l.name}</div>
                  <div className="mono text-white/40 mt-1">{l.company || l.email || "—"}</div>
                </div>
                <div className="space-y-1.5">
                  {[["B", l.bant_budget], ["A", l.bant_authority], ["N", l.bant_need], ["T", l.bant_timeline]].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[16px_1fr_36px] items-center gap-2">
                      <span className="mono text-white/40">{k}</span>
                      <Bar value={v} />
                      <span className="mono text-white/60 text-right">{v}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                    <SelectTrigger data-testid={`status-${l.id}`} className="bg-black border-white/15 rounded-sm mono w-36"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
                      {STATUSES.map(s => <SelectItem key={s} value={s} className="mono">{s.toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1.5 mt-1">
                    <div className="mono text-white/40">SCORE · {avg}</div>
                    {l.deposit_status === "paid" && <span data-testid={`deposit-paid-${l.id}`} className="mono px-1.5 py-0.5 bg-neon text-black">DEPOSIT PAID</span>}
                    {l.deposit_status === "pending" && <span data-testid={`deposit-pending-${l.id}`} className="mono px-1.5 py-0.5 bg-white/10 text-white/70">DEPOSIT PENDING</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`lead-thread-${l.id}`} onClick={() => navigate(`/app/factory/inbox/${l.id}`)} title="Open chat thread" className="mono px-2 py-1 border border-white/20 text-white/70 hover:border-kinetic hover:text-kinetic transition rounded-sm inline-flex items-center gap-1"><MessageSquare className="w-3 h-3"/>CHAT</button>
                  <button data-testid={`lead-triage-${l.id}`} onClick={() => triage(l.id)} title="AI triage" className="mono px-2 py-1 border border-white/20 text-white/70 hover:border-kinetic hover:text-kinetic transition rounded-sm inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/>AI</button>
                  <button data-testid={`lead-convert-${l.id}`} onClick={() => convert(l.id)} title="Convert to project" className="mono px-2 py-1 border border-kinetic/40 text-kinetic hover:bg-kinetic hover:text-black transition rounded-sm inline-flex items-center gap-1"><Wand2 className="w-3 h-3"/>CONVERT</button>
                  <button data-testid={`lead-del-${l.id}`} onClick={() => del(l.id)} className="text-white/40 hover:text-coral"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
